import {Button, Input, CheckBox, ConfirmationDialog, DeleteButton} from '@dv5/components'
import {DiaryItem, TodoItem} from '@dv5/domain'
import {FC, useEffect, useState} from 'react'
import {v4 as uuidv4} from 'uuid'
import {parseContent, saveContent, datetimeToTimestamp, timestampToDatetime} from './diaryUtils'
import {isModPressed, modKeyName} from '../keyboard/platform'
import './DiaryEditor.less'

interface DiaryEditorProps {
    content: string
    setContent: (content: string) => void
    partId?: string
    readOnly?: boolean
}

export const DiaryEditor: FC<DiaryEditorProps> = ({content, setContent, partId, readOnly = false}) => {
    const [items, setItems] = useState<DiaryItem[]>(() => parseContent(content).items)
    const [todos, setTodos] = useState<TodoItem[]>(() => parseContent(content).todos)
    const [newStartTime, setNewStartTime] = useState(timestampToDatetime(Date.now()))
    const [newText, setNewText] = useState('')
    const [newTodoTitle, setNewTodoTitle] = useState('')
    const [showClearConfirmation, setShowClearConfirmation] = useState(false)

    useEffect(() => {
        const parsed = parseContent(content)
        setItems(parsed.items)
        setTodos(parsed.todos)
    }, [content])

    const handleTextChange = (text: string) => {
        if (newText.length === 0 && text.length > 0) {
            setNewStartTime(timestampToDatetime(Date.now()))
        }
        setNewText(text)
    }

    const handleAddItem = () => {
        const startTime = datetimeToTimestamp(newStartTime)
        const duration = Math.floor((Date.now() - startTime) / 1000)
        const newItem: DiaryItem = {
            id: uuidv4(),
            startTime,
            duration,
            text: newText,
        }
        const updatedItems = [...items, newItem]
        setItems(updatedItems)
        saveContent(updatedItems, todos, setContent)
        setNewStartTime(timestampToDatetime(Date.now()))
        setNewText('')
    }

    const handleClearAll = () => {
        setShowClearConfirmation(true)
    }

    const confirmClear = () => {
        setItems([])
        setTodos([])
        saveContent([], [], setContent)
        setShowClearConfirmation(false)
    }

    const cancelClear = () => {
        setShowClearConfirmation(false)
    }

    const handleAddTodo = () => {
        const newTodo: TodoItem = {
            id: uuidv4(),
            title: newTodoTitle.trim(),
            checked: false,
        }
        const updatedTodos = [...todos, newTodo]
        setTodos(updatedTodos)
        saveContent(items, updatedTodos, setContent)
        setNewTodoTitle('')
    }

    const handleToggleTodo = (id: string, checked: boolean) => {
        const updatedTodos = todos.map(todo =>
            todo.id === id ? { ...todo, checked } : todo
        )
        setTodos(updatedTodos)
        saveContent(items, updatedTodos, setContent)
    }

    const handleDeleteTodo = (id: string) => {
        const updatedTodos = todos.filter(todo => todo.id !== id)
        setTodos(updatedTodos)
        saveContent(items, updatedTodos, setContent)
    }

    const handleDeleteItem = (id: string) => {
        const updatedItems = items.filter(item => item.id !== id)
        setItems(updatedItems)
        saveContent(updatedItems, todos, setContent)
    }

    const inputId = partId ? `diary-startTime-${partId}` : 'diary-startTime'

    const hasAnyItems = items.length > 0 || todos.length > 0

    return (
        <div className="diary-editor">
            <div className="diary-editor__columns">
                <div className="diary-editor__column diary-editor__column--items">
                    {!readOnly && (
                        <div className="diary-editor__add-form">
                            <div className="diary-editor__form-row">
                                <Input
                                    id={inputId}
                                    label="Start Time"
                                    value={newStartTime}
                                    placeholder="YYYY-MM-DDTHH:MM"
                                    setValue={() => {}}
                                    onConfirm={() => {}}
                                    onCancel={() => {}}
                                    disabled={true}
                                />
                            </div>
                            <div className="diary-editor__form-row">
                                <label className="diary-editor__label">Text:</label>
                                <textarea
                                    className="diary-editor__textarea"
                                    value={newText}
                                    onChange={(e) => handleTextChange(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (isModPressed(e) && e.key === 'Enter') {
                                            e.preventDefault()
                                            if (newText.trim()) {
                                                handleAddItem()
                                            }
                                        }
                                    }}
                                    placeholder={`Enter diary text... (${modKeyName()}+Enter to add)`}
                                    rows={3}
                                />
                            </div>
                            <Button
                                title="Add Entry"
                                className="diary-editor__add-button"
                                onClick={handleAddItem}
                                disabled={!newText.trim()}
                            />
                        </div>
                    )}

                    {!readOnly && hasAnyItems && (
                        <Button
                            title="Clear All"
                            className="diary-editor__clear-button"
                            onClick={handleClearAll}
                        />
                    )}

                    <h3 className="diary-editor__section-title">Diary</h3>
                    <div className="diary-editor__list">
                        {items.length === 0 && readOnly && (
                            <div className="diary-editor__empty">No diary entries</div>
                        )}
                        {items.length === 0 && !readOnly && (
                            <div className="diary-editor__empty">No entries yet. Add your first entry above.</div>
                        )}
                        {items.map((item, index) => (
                            <div key={item.id} className="diary-editor__item">
                                <div className="diary-editor__item-header">
                                    <span className="diary-editor__item-index">#{index + 1}</span>
                                    <span className="diary-editor__item-header-right">
                                        <span className="diary-editor__item-time">
                                            {new Date(item.startTime).toLocaleString()}
                                            {' ('}{item.duration}s)
                                        </span>
                                        {!readOnly && (
                                            <DeleteButton
                                                onDelete={() => handleDeleteItem(item.id)}
                                                className="diary-editor__item-delete"
                                            />
                                        )}
                                    </span>
                                </div>
                                <div className="diary-editor__item-text">{item.text}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="diary-editor__column diary-editor__column--todos">
                    <h3 className="diary-editor__section-title">Todos</h3>
                    {!readOnly && (
                        <div className="diary-editor__todo-add-form">
                            <input
                                type="text"
                                className="diary-editor__todo-input"
                                value={newTodoTitle}
                                onChange={(e) => setNewTodoTitle(e.target.value)}
                                placeholder="Enter todo title..."
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newTodoTitle.trim()) {
                                        handleAddTodo()
                                    }
                                }}
                            />
                            <Button
                                title="Add Todo"
                                className="diary-editor__todo-add-button"
                                onClick={handleAddTodo}
                                disabled={!newTodoTitle.trim()}
                            />
                        </div>
                    )}
                    <div className="diary-editor__todo-list">
                        {todos.length === 0 && (
                            <div className="diary-editor__empty">No todos yet.</div>
                        )}
                        {todos.map((todo) => (
                            <div key={todo.id} className="diary-editor__todo-item">
                                <CheckBox
                                    label={todo.title}
                                    value={todo.checked}
                                    setValue={(checked: boolean) => handleToggleTodo(todo.id, checked)}
                                />
                                {!readOnly && (
                                    <Button
                                        title="×"
                                        className="diary-editor__todo-delete"
                                        onClick={() => handleDeleteTodo(todo.id)}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {showClearConfirmation && (
                <ConfirmationDialog
                    message="Are you sure you want to clear all diary entries? This cannot be undone."
                    onConfirm={confirmClear}
                    onCancel={cancelClear}
                />
            )}
        </div>
    )
}
